import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';

import {
  RequestSchemaStatus,
  parseApiErrorMessage,
  PaymentEstimateObject,
} from '@liscio/api';
import { useApiClient } from '@liscio/common';
import { Typography } from '@liscio/ui';

import { EditableCurrencyField } from '../EditableCurrencyField/EditableCurrencyField';

export interface EditableEstimatedPaymentFieldProps {
  amountInCents: number | null | undefined;
  paymentEstimate: PaymentEstimateObject;
  quarter: 'q1' | 'q2' | 'q3' | 'q4';
  requestStatus?: string;
  onSuccess?: () => void;
}

export const EditableEstimatedPaymentField = ({
  amountInCents,
  paymentEstimate,
  quarter,
  requestStatus,
  onSuccess,
}: EditableEstimatedPaymentFieldProps) => {
  const isEditable = requestStatus === RequestSchemaStatus.draft;
  const amountInDollars = amountInCents ? amountInCents / 100 : 0;
  const apiClient = useApiClient();

  const { mutateAsync: updatePaymentEstimate, isLoading } = useMutation({
    mutationFn: async (newAmountInDollars: number) => {
      if (!paymentEstimate.uuid) {
        throw new Error('Payment estimate UUID is required');
      }

      const updateData = {
        [`${quarter}_payment_estimate`]: Math.round(newAmountInDollars * 100),
      };

      const response = await apiClient.client.put(
        `/api/v5/firms/payment_estimates/${paymentEstimate.uuid}`,
        updateData
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success('Estimated payment updated successfully');
      onSuccess?.();
    },
    onError: (error: unknown) => {
      const apiErrorMessage = parseApiErrorMessage(error);
      toast.error(apiErrorMessage || 'Failed to update estimated payment');
    },
  });

  const handleSave = async (newAmountInDollars: number) => {
    await updatePaymentEstimate(newAmountInDollars);
  };

  return (
    <EditableCurrencyField
      amountInDollars={amountInDollars}
      isEditable={isEditable}
      isLoading={isLoading}
      onSave={handleSave}
      minWidth={12.5}
      readOnlyDisplay={
        <Typography variant="body1" fontWeight={600}>
          {amountInDollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Typography>
      }
    />
  );
};

export default EditableEstimatedPaymentField;
